import { ref } from 'vue';
import dayjs from 'dayjs';
import qs from 'qs';
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus';
import type { messageType } from 'element-plus';
import { SocketMsgInterface } from '@/lib/typings';
import useClipboard from 'vue-clipboard3';
import { Toast } from 'vant';
import type { ToastType } from 'vant';
import relativeTime from 'dayjs/plugin/relativeTime';
import request from './request';
import apiPath from '@/lib/apiPath';
import { calcListTypeStr, accessSession } from '@/lib/helpers/chat';
import { rootStore } from '@/store/rootStore';
import { chatStore } from '@/store/chatStore';
import { DCCommunityCollectionURL, DCPaymentBaseURL, allRuleOperator, formatTransferSuccessNoticeText } from '@/lib/const';
import { debounce } from 'lodash';
import { uploadFile, dataURLToBlob } from '@/components/Upload/utils';
import QRCode from 'qrcode';

dayjs.extend(relativeTime);

let loadingInstance: any = null;

// 人性化展示时间
export function displayTime(timestamp: number | string, use = 'chatList') {
  const isToday = dayjs.unix(Number(timestamp)).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
  const unixTime = dayjs.unix(Number(timestamp));
  const now = dayjs();
  if (use !== 'chatList') {
    return isToday ? unixTime.format('HH:mm:ss') : unixTime.format('YYYY-MM-DD HH:mm:ss');
  }
  if (unixTime.isSame(now, 'day')) {
    return unixTime.format('HH:mm:ss');
  } else if (unixTime.isSame(now.subtract(1, 'day'), 'day')) {
    return '昨天';
  } else if (unixTime.isAfter(now.subtract(2, 'day').startOf('day')) && unixTime.isBefore(now.startOf('day'))) {
    return '前天';
  } else if (unixTime.isSame(now, 'year')) {
    return unixTime.format('MM月DD日');
  } else {
    return unixTime.format('YYYY-MM-DD');
  }
}

export function formatTime(timestamp: number | string, locale: string = 'YYYY-MM-DD HH:mm:ss') {
  return timestamp ? dayjs.unix(Number(timestamp)).format(locale) : '';
}

export function formatSecond(value: number, lang: string = 'zh') {
  if (!value) {
    return lang == 'math' ? '00:00' : '0秒';
  }
  let second = Number(value);
  let minute = 0;
  let hour = 0;
  if (second > 60) {
    minute = Math.floor(second / 60);
    second = Math.floor(second % 60);
    if (minute > 60) {
      hour = Math.floor(minute / 60);
      minute = Math.floor(minute % 60);
    }
  }
  if (lang == 'math') {
    let res = `${hour > 0 ? hour + ':' : ''}${minute > 0 ? minute : '00'}:${second}`;
    return (
      res
        ?.split(':')
        ?.map((v) => (v.length == 2 ? v : '0' + v))
        ?.join(':') || res
    );
  }
  let result = second + '秒';
  if (minute > 0) {
    result = minute + '分' + result;
  }
  if (hour > 0) {
    result = hour + '時' + result;
  }
  return result;
}

export const getDurationTimeFromNow = (unix: number, needSecond = false, zh = false) => {
  let duration = dayjs().unix() - unix;
  let days,
    hours,
    mins,
    seconds = 0;
  if (duration > 3600 * 24) {
    days = Math.floor(duration / 3600 / 24);
    duration = Math.floor(duration % (3600 * 24));
  }
  if (duration > 3600) {
    hours = Math.floor(duration / 3600);
    duration = Math.floor(duration % 3600);
  }
  if (duration > 60) {
    mins = Math.floor(duration / 60);
    seconds = Math.floor(duration % 60);
  }
  return `${days ? days + (zh ? '天' : 'd') : ''}${hours ? hours + (zh ? '時' : 'h') : ''}${
    mins ? mins + (zh ? '分' : 'm') : ''
  }${needSecond && seconds ? seconds + (zh ? '秒' : 's') : ''}`;
};

// 获取日期 当日起止时间
export function getDateTimeFormat(value: string, type: 'start' | 'end' = 'start', format = 'YYYY-MM-DD HH:mm:ss') {
  if (!value) return undefined;
  if (type == 'start') {
    return format === 'unix' ? dayjs(value).startOf('day').unix() : dayjs(value).startOf('day').format(format);
  } else {
    return format === 'unix' ? dayjs(value).endOf('day').unix() : dayjs(value).endOf('day').format(format);
  }
}

export function formatMoney(value: number) {
  if (value) {
    return Number(value).toLocaleString('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return '￥0';
}

// 是否为正整数
export function isPositiveInteger(val: string | number) {
  return val && /^\d+$/.test(String(val));
}

export function scrollToBottomDistance(id: string) {
  const ele = document.getElementById(id);

  if (ele) {
    return ele.scrollHeight - ele.scrollTop - ele.clientHeight;
  }
  return 0;
}
export function scrollToBottomById(id: string) {
  const ele = document.getElementById(id);
  const chatStoreEx = chatStore();

  if (ele) {
    ele.scrollTop = ele.scrollHeight;
    chatStoreEx.setShowScrollToBottom(false);
  }
}
export async function copyText(text: string, showMsg: boolean = true) {
  if (!text) return;
  try {
    const { toClipboard } = useClipboard();
    await toClipboard(String(text));
    if (showMsg) {
      ElMessage({
        type: 'success',
        message: '複製成功',
      });
    }
  } catch (error) {
    console.log(error);
  }
}

let loadingTimeout: any = null;

export function setLoading(options: { text?: string; lock?: boolean; target?: string } = {}) {
  if (loadingInstance) return;
  loadingInstance = ElLoading.service(
    Object.assign({ lock: true, text: '加載中，請耐心等候...', target: '#main-container' }, options)
  );
  loadingTimeout && clearTimeout(loadingTimeout);
  loadingTimeout = setTimeout(() => {
    cancelLoading();
  }, 2000);
}

export function cancelLoading() {
  loadingInstance && typeof loadingInstance.close === 'function' && loadingInstance.close();
  loadingInstance && (loadingInstance = null);
}

export function getQueryFromUrl(key: any, url = '') {
  let queryString = qs.parse(url ? url : location.search, {
    ignoreQueryPrefix: true,
  });
  if (key instanceof Array) {
    let obj: any = {};
    for (let i of key) {
      obj[i] = queryString[i];
    }
    return obj;
  }
  return queryString[key] || '';
}

export function delQuery(url: string) {
  return url.split('?')[0];
}

export function replaceUrlParam(url: string, paramName: string, paramValue: string) {
  if (!url || !paramName) return url;
  const regex = new RegExp(`([?&])${paramName}=.*?(&|$)`, 'i');
  const separator = url.indexOf('?') !== -1 ? '&' : '?';
  if (url.match(regex)) {
    return url.replace(regex, `$1${paramName}=${paramValue}$2`);
  } else {
    return `${url}${separator}${paramName}=${paramValue}`;
  }
}

export function getExt(fileName: string) {
  return fileName.replace(/.+\./, '').toLowerCase();
}

export function isPicture(fileName: string) {
  const ext = getExt(fileName);
  return ['png', 'jpg', 'jpeg', 'bmp'].includes(ext);
}

export function getFileType(fileName: string) {
  const ext = getExt(fileName);
  if (['png', 'jpg', 'jpeg', 'bmp'].includes(ext)) {
    return 'image';
  } else if (['mp4', 'avi', 'mkv'].includes(ext)) {
    return 'video';
  } else {
    return 'other';
  }
}

export const confirmFn = ({
  title = '提示',
  message = '',
  confirmButtonText = '確定',
  cancelButtonText = '取消',
  onOk = () => {},
  onCancel = () => {},
}) => {
  ElMessageBox.confirm(message, title, {
    confirmButtonText,
    cancelButtonText,
  })
    .then(() => {
      typeof onOk === 'function' && onOk();
    })
    .catch(() => {
      typeof onCancel === 'function' && onCancel();
    });
};

export function getVideoPlayerInfo(file: File) {
  return new Promise((resolve) => {
    const videoElement = document.createElement('video');
    videoElement.src = URL.createObjectURL(file);
    videoElement.muted = true;
    videoElement.play();
    videoElement.addEventListener('loadedmetadata', function () {
      resolve({
        duration: videoElement.duration,
        width: videoElement.videoWidth,
        height: videoElement.videoHeight,
      });
    });
  });
}
export function setVideoCoverImgWithCanvas(canvas: HTMLCanvasElement, url: string, width = 180, height = 100) {
  if (!canvas || !url) return;
  const ctx = canvas.getContext('2d');
  let video: HTMLVideoElement | null = document.createElement('video');
  video.setAttribute('src', url);
  video.setAttribute('width', width + 'px');
  video.setAttribute('height', height + 'px');
  video.setAttribute('preload', 'auto');
  return new Promise((resolve, reject) => {
    const loadeddataHandle = () => {
      video && ctx?.drawImage(video, 0, 0, width, height);
      video?.removeEventListener('loadeddata', loadeddataHandle);
      video = null;
      resolve(true);
    };
    video?.addEventListener('loadeddata', loadeddataHandle);
    const errorHandle = () => {
      if (ctx) {
        ctx.font = '14px 微软雅黑';
        const text = '視頻地址獲取失敗';
        const textWidth = ctx?.measureText(text).width || 1;
        const xPosition = width / 2 - textWidth / 2;
        const yPosition = height / 2;
        ctx.fillStyle = '#fff';
        ctx.fillText(text, xPosition, yPosition);
      }
      video?.removeEventListener('error', errorHandle);
      video = null;
      reject(false);
    };
    video?.addEventListener('error', errorHandle);
  });
}

export function validateData(type: string, val: string) {
  const phonePattern =
    '^(' +
    '(?:\\+86|86)?[-]?(?:13[0-9]|14[01456879]|15[0-35-9]|17[0-8]|18[0-9]|19[0-9])\\d{8}|' + // 中国内地：11位数字,号段 13/14/15/17/18/19
    '(?:\\+852|852)?[-]?(?:[2356789]\\d{7})|' + // 中国香港：8位数字,无号段限制(常见2/3/5/6/7/8/9开头)
    '(?:\\+886|886|0)[-]?9\\d{2}[-]?\\d{3}[-]?\\d{3}|' + // 中国台湾：10位数字(含区号09,去0后9位)
    '(?:\\+853|853)?[-]?(?:6\\d{7}|8\\d{7})|' + // 中国澳门：8位数字,号段6/8 开头
    '(?:\\+65|65)?[-]?(?:6|8|9)\\d{7}|' + // 新加坡：8位数字,号段 6/8/9 开头
    '(?:\\+60|60)?[-]?1[02346789]\\d{1}[-]?\\d{4}[-]?\\d{4}|' + // 马来西亚：10-11 位数字(手机号01x开头,去0后9位)
    '(?:\\+1|1)?[-]?\\d{3}[-]?\\d{3}[-]?\\d{4}|' + // 美国/加拿大：10位数字(区号3位+号码7位,含分隔符)
    '(?:\\+44|44)?[-]?(?:07)\\d{2}[-]?\\d{3}[-]?\\d{4}|' + // 英国：10位数字(去0后,常见7开头)
    '(?:\\+61|61)?[-]?(?:4|5|7|8)\\d{1}[-]?\\d{3}[-]?\\d{5}|' + // 澳大利亚：10位数字(去0后,手机号4/5/7/8 开头)
    '(?:\\+66|66)?[-]?(?:0)?(?:8|9)\\d{1}[-]?\\d{3}[-]?\\d{4}|' + // 泰国：9-10位数字，通常以0开头，如08xxxxxxxx、09xxxxxxxx
    '(?:\\+62|62)?[-]?(?:08)?8\\d{1}[-]?\\d{4,5}[-]?\\d{4}' + // 印尼：10-13 位数字，通常以08开头，如0812xxxxxxxxxx、0857xxxxxxxxxx
    ')$';
  let RegExps: { [propName: string]: RegExp } = {
    phone: new RegExp(phonePattern),
    email:
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  };
  if (type && val) {
    return RegExps[type].test(val);
  }
  return false;
}

/**
 * 从文本中提取邮箱或手机号
 * @param text 输入的文本，可能包含邮箱或手机号
 * @returns 返回第一个有效的邮箱或手机号，如果都没有则返回空字符串
 */
export function extractEmailOrPhone(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // 先尝试匹配邮箱
  const emailPattern = /[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  const emailMatches = text.match(emailPattern);
  if (emailMatches) {
    for (const email of emailMatches) {
      if (validateData('email', email)) {
        return email;
      }
    }
  }

  // 再尝试匹配纯数字手机号（连续数字）
  const phonePattern = /\d{8,15}/g;
  const phoneMatches = text.match(phonePattern);

  if (phoneMatches) {
    for (const phone of phoneMatches) {
      if (validateData('phone', phone)) {
        return phone;
      }
    }
  }

  return '';
}

export function downLoadImage(imgSrc: string, name = '新建圖片') {
  let fileName = /\.(png|PNG)$/.test(name) ? name : name + '.png';
  const img = new Image();
  img.setAttribute('crossOrigin', 'anonymous');
  img.onload = () => {
    let canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    let ctx = canvas.getContext('2d');
    ctx?.drawImage(img, 0, 0, img.width, img.height);
    let url = canvas.toDataURL('image/png');
    let a = document.createElement('a');
    a.download = fileName;
    a.href = url;
    a.click();
  };
  img.src = imgSrc;
}

export async function downloadFile({ type = 'quick_reply', filename = '導出文件', query = {}, url = '' }) {
  const { token } = await request.get(apiPath.download.getDownLoadToken.replace(':type', type));
  if (
    !['business_livecode', 'livecode_statistics', 'scene_statistics', 'scene_robot_statistics'].includes(type) ||
    !url
  ) {
    url = apiPath.download.downloadByType.replace(':type', type);
  }
  let res;
  if (['speed_send_task', 'advanced_send_task'].includes(type)) {
    res = await request.post(
      url,
      {
        token,
        ...query,
      },
      {},
      {
        responseType: 'blob',
      }
    );
  } else {
    res = await request.get(
      url,
      {
        token,
        ...query,
      },
      {
        responseType: 'blob',
      }
    );
  }
  ElMessage.success('導出成功，請移步至【我的下載中心】進行下載');
}

export async function dowloadFileByUrl(url: string, filename: string) {
  if (!url) return;
  return request
    .get(
      url,
      {},
      {
        responseType: 'blob',
      }
    )
    .then((res) => {
      dowloadFileByBlob(res, filename);
    })
    .catch(() => {
      window.open(url);
    });
}
const dowloadFileByBlob = (res: string, filename: string) => {
  const blob = new Blob([res]);
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export function showMicrometersNumber(val: number | string) {
  return val && !isNaN(Number(val)) ? Number(val).toLocaleString() : val;
}

export function validatePageParams(val: string, field = 'from') {
  return getQueryFromUrl(field) === val;
}

export const isProd = !/sandbox|localhost|127\.0.0.1|10.1.200|10.1.161/i.test(location.hostname);
function checkWechatBrowser() {
  if (!isProd) {
    return /iPhone|iPod|Android|ios|iOS|iPad|Backerry|WebOS|Symbian|Windows Phone|Phone|dingtalk|DingTalk/i.test(
      window.navigator.userAgent
    );
  }
  const ua = window.navigator.userAgent.toLowerCase();
  return (/micromessenger/i.test(ua) || /dingtalk/i.test(ua)) && /mobile/.test(ua);
}
export const isWechatBrowser = checkWechatBrowser();

export function showMsg(msg: string, type: messageType, duration = 3000) {
  if (isWechatBrowser) {
    Toast({
      type: (type == 'warning' ? '' : type) as ToastType,
      message: msg,
      duration,
    });
  } else {
    ElMessage({
      type,
      message: msg,
      duration,
    });
  }
}

// 强提醒提示音
interface NotificationItem {
  id: number;
  sessionId?: number;
  groupId?: number;
  instance: Notification;
}
const notificationAudio = new Audio('https://img-fe.tengzhihh.com/audio/c58fb135c2546f.mp3');
const notificationInstanceArr: Array<NotificationItem> = [];

export function requestBrowserNotificationPermission() {
  if (!('Notification' in window)) return Promise.resolve('denied' as NotificationPermission);
  if (Notification.permission !== 'default') return Promise.resolve(Notification.permission);
  return Notification.requestPermission();
}

export function handleBrowserNotification(data: SocketMsgInterface, type: string = 'message') {
  if (!('Notification' in window)) {
    console.log('瀏覽器不支持訊息通知');
    return;
  }
  if (data.data?.is_me) return;
  const target = data?.data?.update_receive || {};
  if (target.text === '訪客已離線') return;
  const noticeText = formatTransferSuccessNoticeText(target.text) || target.text;
  let str = target.nickname ? `${target.nickname}：${noticeText}` : '您有新的未讀訊息';
  if (type === 'userOnline' && calcListTypeStr(target.source) == 'ip') {
    str = `虛擬IP用戶${target.nickname || ''}上線了`;
  }
  const options = {
    body: str,
    silent: false,
    lang: 'ZH',
    // sticky: true,
    // requireInteraction: true,
    icon: import.meta.env.VITE_NOTIFICATION_ICON || import.meta.env.VITE_FAVICON,
  };
  const title = `${import.meta.env.VITE_PROJECT_NAME}訊息提醒`;
  if (Notification.permission !== 'granted') {
    Notification.requestPermission(function (status) {
      if (status == 'granted') {
        const notification = new Notification(title, options);
        notification.onclick = function () {
          handleMessageFocus(data);
        };
        // notification.closeTimer = setTimeout(() => {
        //   notification?.close();
        // }, 3000);
        handleNotificationInstanceArr({
          instance: notification,
          id: target.id,
          sessionId: target.session_id,
          groupId: target.group_id,
        });
      }
    });
  } else {
    const notification = new Notification(title, options);
    notification.onclick = function () {
      handleMessageFocus(data);
    };
    // notification.closeTimer = setTimeout(() => {
    //   notification?.close();
    // }, 3000);
    handleNotificationInstanceArr({
      instance: notification,
      id: target.id,
      sessionId: target.session_id,
      groupId: target.group_id,
    });
  }
}
// 优先关闭重复会话的弹窗
function handleNotificationInstanceArr({
  instance,
  id,
  sessionId,
  groupId,
}: {
  instance: Notification;
  id: number;
  sessionId: number;
  groupId: number;
}) {
  notificationInstanceArr.push({
    id: id,
    sessionId: sessionId,
    groupId: groupId,
    instance: instance,
  });
  let index = -1;
  let idArr = notificationInstanceArr.map((item) => item.id);
  for (let i = 0; i < idArr.length; i++) {
    if (idArr.lastIndexOf(idArr[i]) !== i) {
      index = i;
      break;
    }
  }
  if (notificationInstanceArr.length == 4) {
    if (index !== -1) {
      notificationInstanceArr[index].instance?.close();
      notificationInstanceArr.splice(index, 1);
    } else {
      notificationInstanceArr.shift()?.instance?.close();
    }
  }
  notificationAudio?.play();
}
// 关闭已回复会话消息提醒框
export function handleNotificationInstanceReplyed() {
  const chatStoreEx = chatStore();
  notificationInstanceArr.forEach((item) => {
    if (
      (item.sessionId && item.sessionId === chatStoreEx.currentChatRoomInfo?.sessionId) ||
      (item.groupId && item.groupId === chatStoreEx.currentChatRoomInfo?.groupId)
    ) {
      item.instance?.close();
    }
  });
}

function handleMessageFocus(data: SocketMsgInterface) {
  const target = data?.data?.update_receive || {};
  for (let i = 0; i < notificationInstanceArr.length; i++) {
    if (notificationInstanceArr[i].id == target.id) {
      notificationInstanceArr[i].instance?.close();
      notificationInstanceArr.splice(i, 1);
      i--;
    }
  }
  // 先写入定位信息再聚焦，避免 onfocus / watch 时读不到
  if (window.location.href.indexOf('/chat') === -1) {
    const chatStoreEx = chatStore();
    chatStoreEx.setNotificationMessageInfo(data);
  } else {
    handleLocationSession(data);
  }
  window?.focus();
}
let lcoactionSessionTimer: any = null;
export function handleLocationSession(data: SocketMsgInterface) {
  const chatStoreEx = chatStore();
  const rootStoreEx = rootStore();
  const target = data?.data?.update_receive || {};
  let name = calcListTypeStr(target.source);
  rootStoreEx.initMsgBodyMoreFlag();
  rootStoreEx.initMsgBodyMoreAIMsgFlag();
  // 与列表点击一致，先写入 cache，AccessSuccess 依赖 roomInfoCache 合并会话信息
  chatStoreEx.setRoomInfoCache({
    ...chatStoreEx.roomInfoCache,
    roomFrom: 'personal',
    roomType: name,
    id: target.id,
    sessionId: target.session_id,
    groupId: target.group_id,
    isTop: target.is_top,
    nickName: target.nickname,
    remark: '',
    uid: target.uid,
    type: target.source,
    againReceive: target.again_receive,
    status: target.status,
    robotSerialNo: '',
    robotId: '',
    source: target.source,
    labels: target.labels || [],
  });
  accessSession({
    type: name,
    receive_id: target.id,
    group_id: target.group_id,
    session_id: target.session_id,
    dispatch: target.dispatch,
  }).then(() => {
    chatStoreEx.setCurrentChatRoomInfo({
      roomFrom: 'personal',
      roomType: name,
      id: target.id,
      sessionId: target.session_id,
      groupId: target.group_id,
      isTop: target.is_top,
      nickName: target.nickname,
      remark: '',
      uid: target.uid,
      type: target.type,
      againReceive: target.again_receive,
      status: target.status,
      robotSerialNo: '',
      robotId: '',
      labels: target.labels || [],
    });
    chatStoreEx.clearCurrentChatRoomAIMsgList();
    lcoactionSessionTimer && clearTimeout(lcoactionSessionTimer);
    lcoactionSessionTimer = setTimeout(() => {
      scrollToBottomById('msg-body');
    });
  });
}

export function handleSetPageMsgGroupSetting(field: string, value: string) {
  if (!field) return;
  const obj = JSON.parse(window.localStorage?.getItem('pageMsgGroupTypePreventSetting') || '{}');
  obj[field] = value;
  window.localStorage?.setItem('pageMsgGroupTypePreventSetting', JSON.stringify(obj));
}
export function handleGetPageMsgGroupSetting(field: string) {
  if (!field) return '';
  const obj = JSON.parse(window.localStorage?.getItem('pageMsgGroupTypePreventSetting') || '{}');
  return obj[field] || '';
}

// 初始化微信收款二维码底图
export function initCanvasBackImg(options: any) {
  return new Promise((resolve, reject) => {
    const { canvas, img_url, width, height } = options;
    if (!canvas || !img_url) return;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#FFF';
    ctx.fillRect(0, 0, width, height);
    const img = new Image();
    img.src = img_url;
    img.setAttribute('crossOrigin', 'Anonymous');
    img.onload = () => {
      ctx.drawImage(img, 0, 0, width, height);
      resolve(true);
    };
  });
}
// 收款链接转为图片
export function createCodeImg({
  url = '',
  uploadFlag = 'pasteInsertInputUpload',
  isGroupPayment = false,
  qrcodePosition = {},
}) {
  const cans = document.getElementById('qrcodeCollectionCanvas');
  if (!cans || !url) return;
  const ctx = cans.getContext('2d');
  initCanvasBackImg({
    canvas: cans,
    width: 390,
    height: 694,
    img_url: isGroupPayment
      ? 'https://img-fe.tengzhihh.com/image/177b0db5770c0995b6df4dbf7fee1186-3726x6642.png'
      : 'https://img-fe.tengzhihh.com/image/ea8dfff0472780-1863x3312.jpg',
  }).then(() => {
    QRCode.toDataURL(url).then((res) => {
      let qrcodeImg = new Image();
      qrcodeImg.src = res;
      qrcodeImg.onload = () => {
        let positionOptions = {
          x: 84,
          y: isGroupPayment ? 180 : 260,
          width: 220,
          height: 220,
          ...qrcodePosition,
        };
        ctx.drawImage(qrcodeImg, positionOptions.x, positionOptions.y, positionOptions.width, positionOptions.height);
        const dataUrl = cans.toDataURL('image/jpeg');
        const blobData = dataURLToBlob(dataUrl);
        uploadFile(new File([blobData], '二维码收款.jpeg', { type: blobData.type }), uploadFlag, false);
      };
    });
  });
}

export const downloadWechatQrcode = ({ url, filename = '新建圖片' }) => {
  const width = 230;
  const height = 260;
  const cans = document.createElement('canvas');
  cans.width = width;
  cans.height = height;
  if (!cans || !url) return;
  const ctx = cans.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#000';
  ctx.font = 'bolder 15px 微软雅黑';
  ctx.fillText('請用微信手機端掃碼諮詢客服', 18, 34);
  return QRCode.toDataURL(url).then((res) => {
    let qrcodeImg = new Image();
    qrcodeImg.src = res;
    qrcodeImg.onload = () => {
      ctx.drawImage(qrcodeImg, 15, 50, 200, 200);
      const dataUrl = cans.toDataURL('image/jpeg');
      let a = document.createElement('a');
      a.download = filename;
      a.href = dataUrl;
      a.click();
    };
  });
};

// 判断会话是否指定分组
export function checkSessionInGroup(item: any, type: string) {
  if (!item || !type || item.batch_close_hidden) return false;
  if (type.startsWith('receiveLabel&&')) {
    // 标签分组：-1为所有标签
    let tagId = Number(type.split('&&')[1]);
    return tagId === -1 ? item.labels?.length && !item.is_append : item.labels?.findIndex((v) => v.id === tagId) !== -1;
  } else if (item.is_append) {
    // is_append 接待打了标签，但是超出接待显示时间范围的 只在具体标签分组展示
    return false;
  } else if (type === 'message') {
    // 消息分组：不包含非IP类离线会话
    return (!item.is_offline && item.status !== 3) || calcListTypeStr(item.source) === 'ip';
  } else if (type === 'unread') {
    // 未读分组：未处理且非离线 || IP类未处理
    return (
      (item.begin_wait_time && !item.is_offline && item.status !== 3) ||
      (calcListTypeStr(item.source) === 'ip' && item.begin_wait_time)
    );
  } else if (['personal', 'group'].includes(type)) {
    return type === 'personal' ? !item.group_id : item.group_id;
  } else if (type === 'error') {
    // 离线分组：is_offline || status === 3
    return item.is_offline || item.status === 3;
  } else if (type === 'mark') {
    return item.is_mark;
  }
  return true;
}

export function getDateRangeByShortCut(val: number) {
  let tmp: any = {
    1: { label: '今天', num: 0, unit: 'day' },
    2: { label: '近兩天', num: 1, unit: 'day' },
    3: { label: '最近三天', num: 2, unit: 'day' },
    4: { label: '最近一週', num: 1, unit: 'week' },
    5: { label: '最近一個月', num: 1, unit: 'month' },
    6: { label: '最近三個月', num: 3, unit: 'month' },
    7: { label: '最近半年', num: 6, unit: 'month' },
  };
  if (!val || !tmp[val]) return [];
  let start = dayjs();
  let endDate = new Date(dayjs().endOf('day').valueOf());
  return [new Date(start.subtract(tmp[val].num, tmp[val].unit).startOf('day').valueOf()), endDate];
}

export const showTodayLabel = (date: string) => {
  return !date ? '' : dayjs(date).format('YYYY-MM-DD') == dayjs().format('YYYY-MM-DD') ? '今天' : '';
};

export const isToday = (date: string) => {
  return date && dayjs(date).endOf('day').unix() === dayjs().endOf('day').unix();
};

export function findItemBySecondTreeList(id: number, list: any = [], children = 'children') {
  for (let i = 0; i < list.length; i++) {
    let obj = list[i][children]?.find((v: any) => v.id == id) || {};
    if (obj.id) {
      return obj;
    }
  }
  return {};
}

export function formatterRuleOperator(key: string) {
  return key ? allRuleOperator[key] : '';
}

export function formatterRuleValue(v: any) {
  return v instanceof Array ? v.join('|') : v;
}

// 树形数据前插一行空行
export function handleTreeDataEmptyRow({ list = [], children = 'chidlren', level = 1, deep = false }) {
  let arr = [];
  list?.forEach((item, index) => {
    arr.push({
      empty_row: true,
      row_level: 1,
      levelId: item.levelId ? `${item.levelId}_empty` : `empty_row-${index}-${Math.random()}`,
    });
    if (deep && item[children]?.length) {
      item[children] = handleTreeDataEmptyRow({ list: item[children], children, flag, level: level + 1 });
    }
    arr.push(item);
  });
  return arr;
}

export function handleTreeDataLevelId({ list = [], parentLevel = 'level', row_level = 1, children = 'children' }) {
  list.forEach((item, index) => {
    item.levelId = `${parentLevel}-${index}`;
    item.row_level = row_level;
    if (item[children]?.length) {
      handleTreeDataLevelId({ list: item[children], parentLevel: item.levelId, row_level: row_level + 1, children });
    }
  });
}

export function handleRowStyleWithEmptyRow({ row }) {
  if (row.row_level === 1 && row.empty_row) {
    return {
      height: '16px',
    };
  }
}
export function handleCellStyleWithEmptyRow({ row }) {
  if (row.row_level === 1 && row.empty_row) {
    return {
      height: '16px',
      'line-height': '16px',
      padding: '0px',
    };
  } else {
    return {
      'border-left': '1px solid #ebeef5',
    };
  }
}
export function handleHeaderStyleWidthEmptyRow({ row }) {
  return {
    'border-left': '1px solid #ebeef5',
    'border-top': '1px solid #ebeef5',
  };
}

export const handlerEchartResize = debounce((chart) => {
  chart?.resize();
});

export const handlerEchartResizeListener = (chart) => {
  window.addEventListener('resize', () => handlerEchartResize(chart));
};

/**
 * 根据对应的键名规则从对象提取数据
 * @param {*} object  参数示例：object = { id: 1, name: 'a'}
 * @param {*} keyMaps 参数示例：keyMaps = { id: 'id', name: 'value' }
 * @return 返回示例：{ id: 1, value: 'a' }
 */
export const pickValueByKeys = (object: any = {}, keyMaps: any = {}) => {
  const result: any = {};
  for (let i in keyMaps) {
    if (object[i]) {
      result[keyMaps[i]] = `${object[i]}`;
    }
  }
  return result;
};

export const getGroupPaymentPageUrl = (params: any = {}) => {
  return `${DCPaymentBaseURL}?order_id=${params.order_id}&amount=${params.amount}&receive_id=${
    params.receive_id
  }&payment_channel=${params.payment_channel}&contract_id=${params.contract_id || ''}&fen_qi=${
    params.is_huabei_supported ? 1 : 0
  }`;
};
// 获取社群收款地址（预备订单）
export const getCommunityCollectionPageUrl = (params: any = {}) => {
  return `${DCCommunityCollectionURL}?contract_id=${params.contract_id}&fen_qi=${
    params.is_huabei_supported ? 1 : 0
  }&uuid=${params.uuid}`;
};

// 页面参数缓存
export const savePageParamsStorage = (routePath: string, params = {}) => {
  let key = `${routePath}-pageParams`;
  const info = JSON.parse(window.localStorage.getItem(key) || '{}');
  window.localStorage.setItem(
    key,
    JSON.stringify({
      ...info,
      ...params,
    })
  );
};
export const getPageParamsStorage = (routePath: string) => {
  let key = `${routePath}-pageParams`;
  const info = JSON.parse(window.localStorage.getItem(key) || '{}');
  return info;
};
export const clearPageParamsStorage = (routePath: string) => {
  let key = `${routePath}-pageParams`;
  window.localStorage.clearItem(key);
};

// 获取埋点用户id
export const getSDKUserId = () => {
  if (!window.sense) {
    return undefined;
  }
  let userId = window.sense.getUserCenterID();
  if (!userId) {
    return undefined;
  }
  return userId;
};

export const isValidDomain = (str: string) => {
  // 此正则表达式用于检测是否为有效域名
  const domainRegex = /^(?:www\.)?([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+([a-zA-Z]{2,})(?::\d+)?\/?$/;
  return domainRegex.test(str);
};

// 获取链接中的域名
export const extractDomainFromUrl = (url: string) => {
  const domainRegex = /^(?:(?:https?|ftp):)?\/\/(?:www\.)?([a-zA-Z0-9.-]+(?::\d+)?)\/?/;
  const match = url.match(domainRegex);

  // match[1] 包含了捕获组中的域名部分
  return match ? match[1] : null;
};

// 替换字符串中的链接地址为超链接
export const replaceExtractLinks = (text: string) => {
  if (!text) return text;
  // 匹配以 http 或 https 开头的链接，遇到空白字符或中文字符停止匹配
  const urlRegex = /(https?:\/\/[a-zA-Z0-9.-]+(?:[^\]\】\<\s\u4e00-\u9fa5]+)?)/g;
  const matches = text.match(urlRegex);
  matches?.forEach((v) => {
    text = text.replace(v, `<a href=${v}>${v}</a>`);
  });
  return text;
};

export const findLastIndex = (arr: any[], fn: Function) => {
  if (!Array.isArray(arr) || !arr?.length) return -1;
  for (let i = arr.length - 1; i >= 0; i--) {
    if (fn(arr[i], i, arr)) {
      return i;
    }
  }
  return -1;
};

export const downloadBlobXlsx = (blob, fileName = '新建文件.xlsx') => {
  if (!blob) return;
  const blobData = new Blob([blob], { type: 'application/octet-stream' });
  const downloadLink = document.createElement('a');
  downloadLink.href = window.URL?.createObjectURL(blobData);
  downloadLink.download = fileName;
  downloadLink.click();
  window.URL?.revokeObjectURL(downloadLink.href);
};

// 过滤请求参数- '' || undefined || NaN
export const filterUselessParams = (obj: any = {}) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => filterUselessParams(v));
  } else if (obj && typeof obj === 'object') {
    return Object.entries(obj).reduce((result, [key, value]) => {
      if (typeof value === 'object') {
        result[key] = filterUselessParams(value);
      } else if (Array.isArray(value)) {
        result[key] = value?.map((tmp) => filterUselessParams(tmp));
      } else if (!(typeof value === 'number' && isNaN(value)) && value !== '' && value !== undefined) {
        result[key] = value;
      }
      return result;
    }, {} as any);
  } else {
    return obj;
  }
};

// 检查是否为弱网
export function checkWeakNetwork() {
  const connection = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
  if (!connection) return false;
  // 网络延迟大于500毫秒（断网时网络延迟为0）
  const flag = connection?.rtt && connection?.rtt > 200;
  return flag;
}

// 随机生成密码
/**
 * 生成一个符合要求的随机密码
 * - 长度16-32位
 * - 必须包含数字、大写字母、小写字母和特殊符号
 * @param {number} [length=16] - 密码长度，默认16位
 * @returns {string} 生成的密码
 */
export function generatePassword(length = 16) {
  // 验证长度参数
  if (length < 16 || length > 32) {
    length = 16;
  }

  // 定义字符集
  const chars = {
    numbers: '0123456789',
    lowerCase: 'abcdefghijklmnopqrstuvwxyz',
    upperCase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    special: '~!@#$%^&*()_+-=[]{}|;:,.<>?',
  };

  // 创建密码数组
  let password = new Array(length);

  // 确保至少包含每种字符各一个
  password[0] = getRandomChar(chars.numbers);
  password[1] = getRandomChar(chars.lowerCase);
  password[2] = getRandomChar(chars.upperCase);
  password[3] = getRandomChar(chars.special);

  // 所有可能的字符
  const allChars = Object.values(chars).join('');

  // 填充剩余位置
  for (let i = 4; i < length; i++) {
    password[i] = getRandomChar(allChars);
  }

  // 打乱密码顺序
  shuffleArray(password);

  return password.join('');
}

/**
 * 从给定字符串中随机选择一个字符
 * @param {string} str - 源字符串
 * @returns {string} 随机字符
 */
function getRandomChar(str) {
  return str.charAt(Math.floor(Math.random() * str.length));
}

/**
 * 打乱数组顺序 (Fisher-Yates shuffle算法)
 * @param {Array} array - 要打乱的数组
 */
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

/**
 * 验证密码是否符合要求
 * @param {string} password - 要验证的密码
 * @returns {boolean} 是否符合要求
 */
export function validatePassword(password) {
  if (password.length < 16 || password.length > 32) {
    return false;
  }

  const rules = {
    hasNumber: /[0-9]/,
    hasUpperCase: /[A-Z]/,
    hasLowerCase: /[a-z]/,
    hasSpecial: /[~!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/,
  };

  return Object.values(rules).every((regex) => regex.test(password));
}

// 使用示例：
// const password = generatePassword(); // 生成16位密码
// const password = generatePassword(20); // 生成20位密码
// console.log(password);
// console.log(validatePassword(password)); // true
